import React from "react";
import useSEO from "../hooks/useSEO";
import Reveal from "../components/Reveal";
import { PageHead } from "../components/Shared";

const UPDATED = "September 16, 2026";

export function Privacy() {
  useSEO({
    title: "Privacy Policy",
    description: "Read Victrie's privacy policy to learn how we collect, use, store and protect your personal data in compliance with the Kenya Data Protection Act and GDPR.",
    path: "/privacy",
  });
  return (
    <>
      <PageHead crumb="Privacy Policy" title="Privacy Policy" text={`Last updated: ${UPDATED}`} />
      <section className="section" style={{ paddingTop: 0 }}>
        <Reveal className="container legal">
          <p>Victrie (“we”, “us”, “our”) respects your privacy and is committed to protecting your personal data. This policy explains what information we collect when you visit victrie.com, how we use it and the rights you have under the Kenya Data Protection Act 2019, the EU General Data Protection Regulation (GDPR) and other applicable laws.</p>

          <h2>1. Information we collect</h2>
          <ul>
            <li><strong>Information you provide:</strong> name, email address, phone number, company and project details when you fill in our contact form, subscribe to our newsletter or email us.</li>
            <li><strong>Automatically collected information:</strong> IP address, browser type, device, pages visited, referring URLs and approximate location, collected through cookies and analytics tools.</li>
            <li><strong>Communications:</strong> the content of emails, calls and messages you exchange with us.</li>
          </ul>

          <h2>2. How we use your information</h2>
          <ul>
            <li>To respond to enquiries and provide quotes and services.</li>
            <li>To send newsletters and updates you have opted into (you can unsubscribe at any time).</li>
            <li>To analyse and improve our website, content and services.</li>
            <li>To comply with legal obligations and protect our rights.</li>
          </ul>

          <h2>3. Cookies and advertising</h2>
          <p>We use cookies and similar technologies to understand how visitors use our site and to serve relevant content. Third-party vendors, including Google, may use cookies to serve ads based on your prior visits to this or other websites. Google's use of advertising cookies enables it and its partners to serve ads based on your visits to our site and/or other sites on the internet. You may opt out of personalised advertising by visiting <a href="https://www.google.com/settings/ads" style={{ color: "var(--accent)" }}>Google Ads Settings</a> or <a href="https://www.aboutads.info" style={{ color: "var(--accent)" }}>www.aboutads.info</a>. You can also disable cookies in your browser settings, though some site features may not function correctly.</p>

          <h2>4. Third-party services</h2>
          <p>Our website may use third-party services such as Google Analytics, Google AdSense, Google Maps, and form-processing providers. These providers have their own privacy policies and may collect data in accordance with them. We do not sell your personal data to anyone.</p>

          <h2>5. Data retention and security</h2>
          <p>We retain personal data only as long as necessary for the purposes described above or as required by law. We implement industry-standard technical and organisational measures, including encryption in transit, access controls and regular security reviews, to protect your data from unauthorised access, loss or misuse.</p>

          <h2>6. Your rights</h2>
          <p>Depending on your jurisdiction, you may have the right to access, correct, delete or restrict the processing of your personal data, to object to processing, to data portability and to withdraw consent. To exercise any of these rights, email <a href="mailto:future@victrie.com" style={{ color: "var(--accent)" }}>future@victrie.com</a>. You also have the right to lodge a complaint with the Office of the Data Protection Commissioner of Kenya or your local supervisory authority.</p>

          <h2>7. Children's privacy</h2>
          <p>Our website and services are not directed to children under 18, and we do not knowingly collect personal data from children.</p>

          <h2>8. International transfers</h2>
          <p>Your data may be processed in countries outside your own, including where our cloud providers operate. We ensure appropriate safeguards are in place for such transfers.</p>

          <h2>9. Changes to this policy</h2>
          <p>We may update this policy from time to time. The latest version will always be posted on this page with the date of the last revision.</p>

          <h2>10. Contact us</h2>
          <p>Victrie, Nairobi, Kenya · Email: future@victrie.com · Phone: +254 713 199 322</p>
        </Reveal>
      </section>
    </>
  );
}

export function Terms() {
  useSEO({
    title: "Terms of Service",
    description: "Terms and conditions governing the use of victrie.com and the software development services provided by Victrie, Nairobi, Kenya.",
    path: "/terms",
  });
  return (
    <>
      <PageHead crumb="Terms of Service" title="Terms of Service" text={`Last updated: ${UPDATED}`} />
      <section className="section" style={{ paddingTop: 0 }}>
        <Reveal className="container legal">
          <p>These Terms of Service (“Terms”) govern your use of the website victrie.com (the “Site”) operated by Victrie (“we”, “us”, “our”). By accessing or using the Site you agree to be bound by these Terms.</p>

          <h2>1. Use of the Site</h2>
          <p>You may use the Site for lawful purposes only. You agree not to misuse the Site, interfere with its operation, attempt to gain unauthorised access to any systems, or use automated tools to scrape content without permission.</p>

          <h2>2. Intellectual property</h2>
          <p>All content on the Site, including text, graphics, logos, code and design, is the property of Victrie or its licensors and is protected by copyright and trademark laws. You may not reproduce, distribute or create derivative works from Site content without our written consent.</p>

          <h2>3. Services and engagements</h2>
          <p>Descriptions of services on the Site are for general information. Any software development, design or consulting engagement is governed by a separate written agreement (statement of work or master services agreement) between Victrie and the client, which will set out scope, fees, timelines, intellectual-property ownership and warranties.</p>

          <h2>4. Case studies and testimonials</h2>
          <p>Case studies, metrics and testimonials presented on the Site are illustrative of the types of outcomes we deliver. Results vary by project, and past performance does not guarantee future results.</p>

          <h2>5. Third-party links and advertising</h2>
          <p>The Site may contain links to third-party websites and may display advertisements served by third parties such as Google AdSense. We are not responsible for the content, privacy practices or accuracy of third-party sites or advertisements.</p>

          <h2>6. Disclaimer of warranties</h2>
          <p>The Site is provided “as is” without warranties of any kind, express or implied. We do not warrant that the Site will be uninterrupted, error-free or free of harmful components.</p>

          <h2>7. Limitation of liability</h2>
          <p>To the maximum extent permitted by law, Victrie shall not be liable for any indirect, incidental, special or consequential damages arising out of or related to your use of the Site.</p>

          <h2>8. Governing law</h2>
          <p>These Terms are governed by the laws of the Republic of Kenya. Any disputes shall be subject to the exclusive jurisdiction of the courts of Nairobi, Kenya.</p>

          <h2>9. Changes</h2>
          <p>We may revise these Terms at any time by posting an updated version on this page. Continued use of the Site after changes constitutes acceptance of the revised Terms.</p>

          <h2>10. Contact</h2>
          <p>Questions about these Terms? Email <a href="mailto:future@victrie.com" style={{ color: "var(--accent)" }}>future@victrie.com</a> or call +254 713 199 322.</p>
        </Reveal>
      </section>
    </>
  );
}
